import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { EditDialogState, ProgressModalState } from './types';
import { GOAL_STATUSES, ITEM_CONFIGS } from './constants';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CarePlanEnum } from '../../../Enum/CarePlanEnum';
import { ProviderPreview } from '../../../Models/CarePlans/SchedulePlan.model';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { IntervalTypes } from '../../../utils/assessments';
import { TimeIntervalTypeEnum } from '../../../Enum/TimeIntervalTypeEnum';
import { preventTextInput } from '../../../utils/common';

interface EditDialogProps {
    editDialog: EditDialogState;
    onClose: () => void;
    onSave: () => void;
    onLabelChange: (value: string) => void;
    onStatusChange: (value: string) => void;
    onDueDateChange: (value: Date | null) => void;
    onProviderChange: (value: ProviderPreview | null) => void;
    onTimeIntervalChange: (value: TimeIntervalTypeEnum | null) => void;
    onTimeIntervalInDaysChange: (value: number) => void;
}

/**
 * Generic edit dialog for all item types
 */
export const EditDialog: React.FC<EditDialogProps> = ({
    editDialog,
    onClose,
    onSave,
    onLabelChange,
    onStatusChange,
    onDueDateChange,
    onProviderChange,
    onTimeIntervalChange,
    onTimeIntervalInDaysChange,
}) => {
    const { roles } = useSelector((state: RootState) => state.carePlanUpdate);

    const checkIfDisabled = (dialog: EditDialogState) => {
        if (dialog.type !== 'schedulePlan' && !dialog.label.trim()) return true;
        if (dialog.type === 'goal' && dialog.status === CarePlanEnum.NotMet) return true;
        if (dialog.type === 'schedulePlan' && (!dialog.provider?.Role || !dialog.timeIntervalType || dialog.timeIntervalIndays <= 0)) return true;
        return false;
    };

    return (
        <Dialog open={editDialog.open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {editDialog.item?.Id ? 'Edit' : 'Add'}{' '}
                {editDialog.type === 'goal' ? 'Goal' : ITEM_CONFIGS[editDialog.type as keyof typeof ITEM_CONFIGS]?.label || 'Item'}
            </DialogTitle>
            <DialogContent>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    {editDialog.type !== 'schedulePlan' && (
                        <TextField label="Name" value={editDialog.label} onChange={(e) => onLabelChange(e.target.value)} fullWidth margin="normal" autoFocus />
                    )}
                    {editDialog.type === 'goal' && editDialog.item?.Id && (
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Status</InputLabel>
                            <Select value={editDialog.status} onChange={(e) => onStatusChange(e.target.value as string)} label="Status">
                                {GOAL_STATUSES.filter((status) => status.value !== CarePlanEnum.NotMet && status.value !== CarePlanEnum.Deleted).map(
                                    (status) => (
                                        <MenuItem key={status.value} value={status.value}>
                                            {status.label}
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                    )}
                    {editDialog.type === 'goal' && (
                        <DatePicker
                            label="Due Date"
                            value={editDialog.dueDate || null}
                            onChange={(date) => onDueDateChange(date)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    sx: { my: 2 },
                                },
                            }}
                        />
                    )}
                    {editDialog.type === 'schedulePlan' && (
                        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                            <InputLabel id="role-select">Role</InputLabel>
                            <Select
                                labelId="role-select"
                                id="role-select"
                                value={Boolean(editDialog.provider?.Role) ? editDialog.provider.Role : ''}
                                label="Role"
                                name="Role"
                                onChange={(e) => onProviderChange({ ...editDialog.provider, Role: e.target.value } as ProviderPreview)}
                            >
                                {roles.map((role) => (
                                    <MenuItem key={role.Id} value={role.Name}>
                                        {role.Name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {editDialog.type === 'schedulePlan' && (
                        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                            <InputLabel id="time-interval-select">Time Interval</InputLabel>
                            <Select
                                labelId="time-interval-select"
                                id="time-interval-select"
                                value={editDialog.timeIntervalType || ''}
                                label="Time Interval"
                                name="TimeIntervalType"
                                onChange={(e) => onTimeIntervalChange(e.target.value as TimeIntervalTypeEnum)}
                            >
                                {IntervalTypes.map((types) => (
                                    <MenuItem key={types.Name} value={types.Id}>
                                        {types.Name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {editDialog.type === 'schedulePlan' && (
                        <TextField
                            variant="outlined"
                            size="small"
                            label={`Time Interval In ${
                                editDialog.timeIntervalType === TimeIntervalTypeEnum.Week
                                    ? 'weeks'
                                    : editDialog.timeIntervalType === TimeIntervalTypeEnum.Day
                                    ? 'days'
                                    : 'months'
                            }`}
                            name="TimeIntervalIndays"
                            type="number"
                            onKeyPress={preventTextInput}
                            inputProps={{ min: 0 }}
                            value={Boolean(editDialog.timeIntervalIndays) ? editDialog.timeIntervalIndays : ''}
                            sx={{ mt: 2 }}
                            onChange={(e) => onTimeIntervalInDaysChange(Number(e.target.value))}
                        />
                    )}
                </LocalizationProvider>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ minWidth: '120px' }} variant="outlined" color="primary">
                    Cancel
                </Button>
                <Button onClick={onSave} sx={{ minWidth: '120px' }} variant="contained" disabled={checkIfDisabled(editDialog)} color="primary">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface ProgressModalProps {
    progressModal: ProgressModalState;
    onClose: () => void;
    onSave: () => void;
    onValueChange: (value: string) => void;
}

/**
 * Modal for adding/editing progress notes
 */
export const ProgressModal: React.FC<ProgressModalProps> = ({ progressModal, onClose, onSave, onValueChange }) => (
    <Dialog open={progressModal.open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>{progressModal.progressId ? 'Edit Progress Note' : 'Add Progress Note'}</DialogTitle>
        <DialogContent>
            <TextField
                label="Progress Note"
                value={progressModal.value}
                onChange={(e) => onValueChange(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                autoFocus
                sx={{ mt: 2 }}
            />
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} variant="outlined" color="primary" sx={{ minWidth: '120px' }}>
                Cancel
            </Button>
            <Button onClick={onSave} variant="contained" sx={{ minWidth: '120px' }} disabled={!progressModal.value.trim()}>
                Save
            </Button>
        </DialogActions>
    </Dialog>
);
